import { observer } from "mobx-react";
import React, { useContext, useEffect } from "react";
import {
  Container,
  Stack,
  Title,
  Text,
  Button,
  Box,
  Paper,
  Group,
  CopyButton,
} from "@mantine/core";
import { IconCheck, IconCopy, IconLink } from "@tabler/icons-react";
import { SatoshiConverterStoreContext } from "../stores/SatoshiConverterStore";
import SatoshiConverterBackground from "./SatoshiConverterBackground";
import ConverterWidget from "./ConverterWidget";
import SatoshiExplainer from "./SatoshiExplainer";
import CurrencyNav from "./CurrencyNav";
import LanguageToggle from "./LanguageToggle";
import { t } from "../helpers/translations";
import RizfulPitch from "../../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, {
  hasServerRenderedBlock,
} from "../../helpers/ServerRenderedBlock";
import ToolLogo from "../../helpers/ToolLogo";

const SatoshiConverterContainer: React.FC = observer(() => {
  const store = useContext(SatoshiConverterStoreContext);

  useEffect(() => {
    store.hydrateFromPage();
  }, []);

  const code = store.currencyCode.toUpperCase();
  const name = store.currencyName;
  const s = store.strings;

  return (
    <SatoshiConverterBackground>
      <Container size={700} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {/* Language Toggle */}
          <LanguageToggle />

          {/* H1 + live rate come from Rails (satoshi_converter/_live_rate) */}
          {hasServerRenderedBlock("ssr-converter-answer") ? (
            <ServerRenderedBlock id="ssr-converter-answer" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                {s.convertSatoshisTo} {code}
              </Title>
              <Text c="dimmed" size="sm">
                {t(s.instantlyConvert, { name, code })}
              </Text>
            </Box>
          )}

          <ConverterWidget />

          {/* Copy link button - show when there's a conversion */}
          {(store.satsInput && store.satsInput !== "0") ||
          (store.fiatInput && store.fiatInput !== "0") ? (
            <Paper
              p="md"
              radius="md"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <IconLink size={18} color="orange" />
                  <Text size="sm" c="dimmed">
                    {s.shareLinkLabel}
                  </Text>
                </Group>
                <CopyButton value={store.shareableUrl}>
                  {({ copied, copy }) => (
                    <Button
                      variant={copied ? "filled" : "outline"}
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      leftSection={
                        copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )
                      }
                      onClick={copy}
                    >
                      {copied ? s.linkCopied : s.copyLink}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text
                size="xs"
                c="dimmed"
                mt="xs"
                style={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  opacity: 0.7,
                }}
              >
                {store.shareableUrl}
              </Text>
            </Paper>
          ) : null}

          <ServerRenderedBlock id="ssr-converter-amounts" />

          {/* sats-per-dollar history + FAQ: English USD page only */}
          <ServerRenderedBlock id="ssr-converter-guide" />

          <SatoshiExplainer />

          <CurrencyNav />


          <RizfulPitch locale={store.effectiveLocale} />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </SatoshiConverterBackground>
  );
});

export default SatoshiConverterContainer;
